import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { U2bValidators } from '@shared/validators/validators';
import { Observable } from 'rxjs';
import { ProductsApiService } from '../../../@shared/services';
import { debounceTime, map, startWith, switchMap } from 'rxjs/operators';
import { IProduct, Product } from '@shared/models/product';
import { DEFAULT_DEBOUNCE_TIME } from '../../../@shared/constants';
import { isString } from '@shared/functions/is-string';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { U2bProductValidators } from '@shared/validators/product/product-validators';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { TranslationService } from '@core/translation/translation.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { BcmDynamicPrice, BcmDynamicPriceContext } from '@shared/models/bcm-dynamic-price';
import { BerthBoatAssignment } from '@shared/models/berth-boat-assignment';
import { DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';

@Component({
    selector: 'form-widget-product-list',
    templateUrl: './form-widget-product-list.component.html',
    standalone: false
})
export class FormWidgetProductListComponent implements OnInit, OnDestroy {

    @Input()
    headline?: string;

    @Input()
    parentFormGroup: UntypedFormGroup;

    @Input()
    singleSelection = false;

    @Input()
    appearance: MatFormFieldAppearance = 'outline';

    /**
     * Opt-in, because the second host of this widget (the voucher form) picks a product to discount
     * and has no booking context to evaluate rules against. (#6959)
     */
    @Input()
    enableDynamicPrice = false;

    @Input()
    dynamicPriceContext: BcmDynamicPriceContext;

    @Input()
    berthAssignments: BerthBoatAssignment[];

    @Input()
    defaultBerthReservationTimeUnit: DefaultBerthReservationTimeUnit;

    productsForm: UntypedFormGroup;

    filteredProductsList$: Observable<Product[]>[] = [];

    products$: Observable<Product[]>;

    captureProducts: boolean;

    /**
     * Toggle state per row. Seeded to `true` when a product with rules is picked, so the criteria are
     * asked right away instead of hiding behind a toggle the user has to find first. (#6959)
     */
    useDynamicPrice: boolean[] = [];

    /** Rows whose dynamic price is missing a required criterion - the host blocks saving on this. */
    dynamicPriceHasError: boolean[] = [];

    @Output()
    dynamicPriceErrorChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(private _formBuilder: UntypedFormBuilder,
                private _confirmDialogService: ConfirmDialogService,
                private _productsApiService: ProductsApiService,
                private _tolgee: TolgeeService) {
    }

    ngOnInit(): void {
        if (this.headline === undefined || this.headline === null) {
            this.headline = this._tolgee.translate('workflows.formWidgets.productList.headline');
        }
        setTimeout(() => {
            this.parentFormGroup.addControl('productsForm', this.productsForm);
        });

        this._createForm();
    }

    ngOnDestroy(): void {
        this.parentFormGroup.removeControl('productsForm');
    }

    public displayProductWith(product: IProduct): string {
        return product ? product.name : '';
    }

    /** Whether the criteria panel applies to this row at all. */
    hasDynamicPrice(item: UntypedFormGroup): boolean {
        return this.enableDynamicPrice && !!item.get('product').value?.hasDynamicPrice;
    }

    onDynamicPriceChanged(index: number, dynamicPrice: BcmDynamicPrice): void {
        const item = this._itemAt(index);

        if (!item || !this.useDynamicPrice[index]) {
            return;
        }

        item.get('dynamicPrice').setValue(dynamicPrice, {emitEvent: false});
    }

    onUseDynamicPriceChanged(index: number, useDynamicPrice: boolean): void {
        // The panel re-emits this on every value change of its own form, so only act on a real flip.
        if (this.useDynamicPrice[index] === useDynamicPrice) {
            return;
        }

        this.useDynamicPrice[index] = useDynamicPrice;

        const item = this._itemAt(index);

        if (!item || useDynamicPrice) {
            return;
        }

        // Back to the product's fixed price - the panel only ever writes the rule price.
        item.get('dynamicPrice').setValue(null, {emitEvent: false});
        item.get('price').setValue(item.get('product').value?.price ?? null, {emitEvent: false});
    }

    onDynamicPriceErrorChanged(index: number, hasError: boolean): void {
        if (this.dynamicPriceHasError[index] === hasError) {
            return;
        }

        this.dynamicPriceHasError[index] = hasError;
        this.dynamicPriceErrorChanged.next(this.dynamicPriceHasError.some(Boolean));
    }

    private _itemAt(index: number): UntypedFormGroup {
        return (this.productsForm.get('products') as UntypedFormArray).at(index) as UntypedFormGroup;
    }

    private _createForm(): void {
        this.productsForm = this._formBuilder.group({
            products: this._formBuilder.array([])
        });

        (this.productsForm.get('products') as UntypedFormArray).push(this.createForm());
        this._loadProducts();
    }

    addProduct(event): void {
        event.preventDefault();
        event.stopPropagation();
        (this.productsForm.get('products') as UntypedFormArray).push(this.createForm());
    }

    removeProduct(event, index: number): void {
        event.preventDefault();
        event.stopPropagation();

        this._confirmDialogService
            .useWarnTheme()
            .setTitle(TranslationService.translate('common.confirm.removeProductFromFormTitle'))
            .setBody(TranslationService.translate('common.confirm.removeProductFromFormBody'))
            .openWithCallback(() => {
                this.filteredProductsList$.splice(index);
                this.useDynamicPrice.splice(index, 1);
                this.dynamicPriceHasError.splice(index, 1);
                this.dynamicPriceErrorChanged.next(this.dynamicPriceHasError.some(Boolean));
                (this.productsForm.get('products') as UntypedFormArray).removeAt(index);
                (this.productsForm.get('products') as UntypedFormArray).markAsDirty();
            });
    }

    private createForm(): UntypedFormGroup {

        const fields: { [key: string]: UntypedFormControl } = {
            product: new UntypedFormControl(null, [
                U2bValidators.required(TranslationService.translate('common.validationMsg.selectProduct')),
                U2bProductValidators.productExists(),
            ]),
            quantity: new UntypedFormControl(null, [
                U2bValidators.required(TranslationService.translate('common.validationMsg.quantityUnitsRequired')),
            ]),
            // Effective unit price of the row - the product's own price until the dynamic price panel
            // resolves a rule price into it. (#6959)
            price: new UntypedFormControl(null),
            dynamicPrice: new UntypedFormControl(null),
        };

        const formGroup = new UntypedFormGroup(fields);
        const index = this.filteredProductsList$.length;
        const productField = formGroup.get('product');

        this.useDynamicPrice.push(false);
        this.dynamicPriceHasError.push(false);

        let previousProduct: Product | null = null;

        productField.valueChanges.subscribe((product: Product) => {
            if (isString(product) || !product) {
                return;
            }

            formGroup.get('price').setValue(product.price ?? null, {emitEvent: false});
            formGroup.get('dynamicPrice').setValue(null, {emitEvent: false});

            // Seed the toggle only while no panel is mounted for this row - once one is, its own
            // toggle owns the state and overwriting it here would desync flag and checkbox.
            const panelMounted = this.enableDynamicPrice && !!previousProduct?.hasDynamicPrice;

            if (!panelMounted) {
                this.useDynamicPrice[index] = this.enableDynamicPrice && !!product.hasDynamicPrice;
            }

            previousProduct = product;
        });

        this.filteredProductsList$.push(
            productField.valueChanges
                .pipe(
                    startWith(this.products$),
                    debounceTime(DEFAULT_DEBOUNCE_TIME),
                    switchMap((value) => isString(value) ? this._filterProducts(value) : this.products$)
                )
        );

        return formGroup;
    }

    private _filterProducts(name = ''): Observable<Product[]> {
        return this.products$
            .pipe(map((products: Product[]) => Product.filterProducts(products, name)));
    }

    private _loadProducts(): void {
        this.products$ = this._productsApiService.getAll();
    }
}
